/**
 * ArkUI.Floating - 通用浮动定位工具
 * 基于 @floating-ui/dom 1.7.4
 *
 * 支持场景：Menu, Select, Picker, Popover, Tooltip, Dropdown
 */

import {
    computePosition,
    autoUpdate,
    offset,
    flip,
    shift,
    size,
    limitShift,
    arrow
} from '@floating-ui/dom';

const instances = new Map();

/**
 * 视口避让边界。软键盘打开时（ArkUI.Keyboard，Android POS）键盘是盖在界面上的一层、视口不变小，
 * 键盘那一截不算可用空间：底边多让出键盘高度，下拉就不会展开到键盘后面（向上翻或收矮）。
 * 写成函数（floating-ui 的 derivable options）：每次定位现取，键盘起落时 ark-keyboard.js 派发 scroll 触发重算。
 */
function boundaryPadding() {
    const kb = window.ArkUI && window.ArkUI.Keyboard ? window.ArkUI.Keyboard.height() : 0;
    return {top: 8, right: 8, left: 8, bottom: 8 + kb};
}

/**
 * 默认配置
 */
const defaultOptions = {
    placement: 'bottom-start',
    offset: 4,
    flip: true,
    shift: true,
    maxHeight: 320,
    maxWidth: null,
    matchTriggerWidth: false,
    minWidth: 120,           // 最小宽度，0 = 自适应内容（用于 Tooltip）
    arrow: false,
    arrowPadding: 8,
    closeOnOutsideClick: true,
    closeOnEscape: true,
    closeOnScroll: false,
    touchOptimized: false,
    animation: 'fade-scale', // 'fade' | 'fade-scale' | 'slide' | 'none'
    animationDuration: 150
};

/**
 * 显示浮动面板
 * @param {string} panelId - 面板元素 ID
 * @param {HTMLElement} triggerElement - 触发器元素
 * @param {object} dotNetRef - .NET 对象引用
 * @param {object} options - 配置选项
 * @returns {Promise<boolean>} 是否成功显示
 */
export async function show(panelId, triggerElement, dotNetRef, options = {}) {
    const panel = document.getElementById(panelId);
    if (!panel || !triggerElement) {
        console.warn('[ArkFloating] Panel or trigger not found:', panelId);
        return false;
    }

    // 合并配置
    const config = {...defaultOptions, ...options};

    // 如果已存在实例，先清理
    if (instances.has(panelId)) {
        await hide(panelId, false);
    }

    // 准备面板样式
    const triggerWidth = triggerElement.offsetWidth;

    // 计算宽度样式
    let widthStyle, minWidthStyle;

    if (config.matchTriggerWidth) {
        // Select 模式：匹配触发器宽度
        widthStyle = `${triggerWidth}px`;
        minWidthStyle = '';
    } else {
        // Menu/Dropdown/Tooltip 模式
        widthStyle = 'auto';
        minWidthStyle = config.minWidth > 0 ? `${config.minWidth}px` : 'auto';
    }

    Object.assign(panel.style, {
        position: 'fixed',
        visibility: 'hidden',
        display: 'block',
        zIndex: 'var(--z-index-dropdown, 1300)',
        width: widthStyle,
        minWidth: minWidthStyle,
        maxWidth: config.maxWidth ? `${config.maxWidth}px` : '',
        maxHeight: '',
        opacity: '0',
        transform: getInitialTransform(config.animation, config.placement)
    });

    // 获取面板自然高度用于 flip 判断
    const naturalHeight = panel.scrollHeight;

    // 构建 middleware
    const middleware = [
        offset(config.offset)
    ];

    if (config.flip) {
        middleware.push(flip(() => ({
            padding: boundaryPadding(),
            fallbackPlacements: getFallbackPlacements(config.placement),
            fallbackStrategy: 'bestFit'
        })));
    }

    if (config.shift) {
        middleware.push(shift(() => ({
            padding: boundaryPadding(),
            limiter: limitShift()
        })));
    }

    // 尺寸调整 - 必须在 flip 之后
    middleware.push(size(() => ({
        padding: boundaryPadding(),
        apply({availableHeight, availableWidth, elements}) {
            const effectiveMaxHeight = config.maxHeight
                ? Math.min(config.maxHeight, availableHeight - 8)
                : availableHeight - 8;

            Object.assign(elements.floating.style, {
                maxHeight: `${effectiveMaxHeight}px`,
                overflowY: effectiveMaxHeight < naturalHeight ? 'auto' : 'visible'
            });
        }
    })));

    // 箭头支持
    let arrowElement = null;
    if (config.arrow) {
        arrowElement = panel.querySelector('[data-floating-arrow]');
        if (arrowElement) {
            middleware.push(arrow({
                element: arrowElement,
                padding: config.arrowPadding
            }));
        }
    }

    // 位置计算函数
    const updatePosition = async () => {
        const {x, y, placement: finalPlacement, middlewareData} = await computePosition(
            triggerElement,
            panel,
            {
                placement: config.placement,
                strategy: 'fixed',
                middleware
            }
        );

        Object.assign(panel.style, {
            left: `${x}px`,
            top: `${y}px`
        });

        // 设置数据属性
        panel.dataset.placement = finalPlacement;
        panel.dataset.side = finalPlacement.split('-')[0];

        // 箭头位置
        if (arrowElement && middlewareData.arrow) {
            const {x: arrowX, y: arrowY} = middlewareData.arrow;
            const side = finalPlacement.split('-')[0];
            const staticSide = {
                top: 'bottom',
                right: 'left',
                bottom: 'top',
                left: 'right'
            }[side];

            Object.assign(arrowElement.style, {
                left: arrowX != null ? `${arrowX}px` : '',
                top: arrowY != null ? `${arrowY}px` : '',
                right: '',
                bottom: '',
                [staticSide]: '-4px'
            });
        }
    };

    // 首次定位
    await updatePosition();

    // 显示动画
    panel.style.visibility = 'visible';

    // 触发重排后添加动画
    requestAnimationFrame(() => {
        // 时长走 ark-motion.css 的 token（fast 档）：config.animationDuration 只保留为历史参数，不再决定时长 ——
        // 各组件传的 100 / 150 与 .ark-floating-open 的 keyframe 各说各的，是 2026-09-28 盘点出的错位之一。
        panel.style.transition = 'opacity var(--ark-dur-fast) var(--ark-ease-enter), transform var(--ark-dur-fast) var(--ark-ease-enter)';
        panel.style.opacity = '1';
        panel.style.transform = 'none';
        panel.classList.add('ark-floating-open');
    });

    // 自动更新位置
    const cleanup = autoUpdate(triggerElement, panel, updatePosition, {
        ancestorScroll: !config.closeOnScroll,
        ancestorResize: true,
        elementResize: true,
        layoutShift: true
    });

    // 事件处理器
    const handleOutsideClick = (event) => {
        // 使用 pointerdown 而不是 click，响应更快
        if (!panel.contains(event.target) && !triggerElement.contains(event.target)) {
            dotNetRef?.invokeMethodAsync('OnOutsideClick').catch(() => {
            });
        }
    };

    const handleEscape = (event) => {
        if (event.key === 'Escape') {
            event.preventDefault();
            dotNetRef?.invokeMethodAsync('OnEscapeKey').catch(() => {
            });
        }
    };

    const handleScroll = (event) => {
        if (config.closeOnScroll && !panel.contains(event.target)) {
            dotNetRef?.invokeMethodAsync('OnScrollClose').catch(() => {
            });
        }
    };

    // iframe 内的 pointerdown 不会冒泡到外层 document（如 POS 报表纸是 srcdoc iframe，
    // 铺满面板下方整个内容区 —— 点纸关不掉浮层）。同源 iframe 直接在其文档上补挂监听：
    // 事件目标必然不在面板/触发器内，命中即关闭。跨域 iframe 访问 contentDocument 会抛错，跳过。
    const handleFrameClick = () => {
        dotNetRef?.invokeMethodAsync('OnOutsideClick').catch(() => {
        });
    };
    const frameDocs = [];

    // 延迟绑定事件，避免立即触发
    setTimeout(() => {
        if (config.closeOnOutsideClick) {
            document.addEventListener('pointerdown', handleOutsideClick, true);
            document.querySelectorAll('iframe').forEach(frame => {
                if (panel.contains(frame)) return;
                try {
                    const doc = frame.contentDocument;
                    if (doc) {
                        doc.addEventListener('pointerdown', handleFrameClick, true);
                        frameDocs.push(doc);
                    }
                } catch {
                }
            });
        }
        if (config.closeOnEscape) {
            document.addEventListener('keydown', handleEscape);
        }
        if (config.closeOnScroll) {
            document.addEventListener('scroll', handleScroll, true);
        }
    }, 0);

    // 触摸优化
    if (config.touchOptimized) {
        panel.classList.add('ark-floating-touch');
        applyTouchOptimizations(panel);
    }

    // 保存实例
    instances.set(panelId, {
        cleanup,
        handleOutsideClick,
        handleFrameClick,
        frameDocs,
        handleEscape,
        handleScroll,
        config,
        dotNetRef,
        triggerElement,
        panel
    });

    return true;
}

/**
 * 隐藏浮动面板
 * @param {string} panelId - 面板元素 ID
 * @param {boolean} animate - 是否使用动画
 */
export async function hide(panelId, animate = true) {
    const instance = instances.get(panelId);
    const panel = instance?.panel || document.getElementById(panelId);

    if (panel && animate && instance?.config) {
        // 退出动画
        const placement = panel.dataset.placement || instance.config.placement;
        panel.style.opacity = '0';
        panel.style.transform = getInitialTransform(instance.config.animation, placement);
        panel.style.transition = 'opacity var(--ark-dur-exit) var(--ark-ease-exit), transform var(--ark-dur-exit) var(--ark-ease-exit)';
        panel.classList.remove('ark-floating-open');

        // 等退场播完（exit 档 100ms，与 MotionTokens.ExitMs 同值）；动效关闭时 CSS 时长为 0，不等
        if (window.ArkUI.Motion?.enabled() !== false) {
            await new Promise(resolve => setTimeout(resolve, 100));
        }
    }

    if (panel) {
        panel.style.display = 'none';
        panel.classList.remove('ark-floating-touch');
    }

    if (instance) {
        instance.cleanup?.();
        document.removeEventListener('pointerdown', instance.handleOutsideClick, true);
        instance.frameDocs?.forEach(doc => {
            try {
                doc.removeEventListener('pointerdown', instance.handleFrameClick, true);
            } catch {
            }
        });
        document.removeEventListener('keydown', instance.handleEscape);
        document.removeEventListener('scroll', instance.handleScroll, true);
        instances.delete(panelId);
    }
}

/**
 * 更新面板位置
 * @param {string} panelId - 面板元素 ID
 */
export async function update(panelId) {
    const instance = instances.get(panelId);
    if (instance) {
        // autoUpdate 会自动处理，这里主要用于手动触发
    }
}

/**
 * 销毁实例
 * @param {string} panelId - 面板元素 ID
 */
export function destroy(panelId) {
    hide(panelId, false);
}

/**
 * 检查面板是否打开
 * @param {string} panelId - 面板元素 ID
 * @returns {boolean}
 */
export function isOpen(panelId) {
    return instances.has(panelId);
}

/**
 * 滚动到指定元素
 * @param {string} containerId - 容器元素 ID
 * @param {string} selector - 目标元素选择器
 * @param {string} behavior - 滚动行为 'auto' | 'smooth' | 'instant'
 */
export function scrollToElement(containerId, selector, behavior = 'instant') {
    const container = document.getElementById(containerId);
    if (!container) return;

    const target = container.querySelector(selector);
    if (target) {
        target.scrollIntoView({block: 'center', behavior});
    }
}

/**
 * 滚动多个容器到选中项
 * @param {object} scrollTargets - { containerId: selector } 映射
 */
export function scrollToSelected(scrollTargets) {
    Object.entries(scrollTargets).forEach(([containerId, selector]) => {
        scrollToElement(containerId, selector, 'instant');
    });
}

// ============ 辅助函数 ============

/**
 * 获取回退位置列表
 */
function getFallbackPlacements(placement) {
    const map = {
        'bottom': ['top', 'bottom-start', 'bottom-end', 'top-start', 'top-end'],
        'bottom-start': ['top-start', 'top', 'bottom-end', 'top-end'],
        'bottom-end': ['top-end', 'top', 'bottom-start', 'top-start'],
        'top': ['bottom', 'top-start', 'top-end', 'bottom-start', 'bottom-end'],
        'top-start': ['bottom-start', 'bottom', 'top-end', 'bottom-end'],
        'top-end': ['bottom-end', 'bottom', 'top-start', 'bottom-start'],
        'left': ['right', 'left-start', 'left-end', 'right-start', 'right-end'],
        'left-start': ['right-start', 'right', 'left-end', 'right-end'],
        'left-end': ['right-end', 'right', 'left-start', 'right-start'],
        'right': ['left', 'right-start', 'right-end', 'left-start', 'left-end'],
        'right-start': ['left-start', 'left', 'right-end', 'left-end'],
        'right-end': ['left-end', 'left', 'right-start', 'left-start']
    };
    return map[placement] || ['top', 'bottom'];
}

/**
 * 获取初始变换
 */
function getInitialTransform(animation, placement) {
    const side = placement?.split('-')[0] || 'bottom';

    switch (animation) {
        case 'fade-scale':
            return 'scale(0.95)';
        case 'slide':
            switch (side) {
                case 'top':
                    return 'translateY(8px)';
                case 'bottom':
                    return 'translateY(-8px)';
                case 'left':
                    return 'translateX(8px)';
                case 'right':
                    return 'translateX(-8px)';
                default:
                    return 'translateY(-8px)';
            }
        case 'none':
        case 'fade':
        default:
            return 'none';
    }
}

/**
 * 应用触摸优化
 */
function applyTouchOptimizations(panel) {
    // 添加触摸友好的滚动
    const scrollables = panel.querySelectorAll('[data-scrollable]');
    scrollables.forEach(el => {
        el.style.webkitOverflowScrolling = 'touch';
        // 启用滚动吸附
        if (el.dataset.scrollSnap !== 'false') {
            el.style.scrollSnapType = 'y proximity';
        }
    });

    // 防止触摸穿透到背景
    const preventTouchThrough = (e) => {
        const scrollable = e.target.closest('[data-scrollable]');
        if (!scrollable) {
            const isInteractive = e.target.closest('button, a, input, [tabindex]');
            if (!isInteractive) {
                e.preventDefault();
            }
        }
    };

    panel.addEventListener('touchmove', preventTouchThrough, {passive: false});

    // 存储清理函数
    panel._touchCleanup = () => {
        panel.removeEventListener('touchmove', preventTouchThrough);
    };
}

// ============ 全局挂载 ============

if (typeof window !== 'undefined') {
    window.ArkUI = window.ArkUI || {};
    window.ArkUI.Floating = {
        show,
        hide,
        update,
        destroy,
        isOpen,
        scrollToElement,
        scrollToSelected
    };
}

export default {
    show,
    hide,
    update,
    destroy,
    isOpen,
    scrollToElement,
    scrollToSelected
};